
import React from 'react';
import { Check, X, HelpCircle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { cn } from '@/lib/utils';
import CTAButton from '../CTAButton';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../ui/card';
import { PlanData, PlanName, PlanFeature } from './types';

interface PlanCardProps {
  plan: PlanData;
  featureCategories: { name: string; features: PlanFeature[] }[];
}

const PlanCard = ({ plan, featureCategories }: PlanCardProps) => {
  const renderFeatureValue = (value: string | number | boolean) => {
    if (typeof value === 'boolean') {
      return value ? 
        <div className="flex justify-center">
          <Check className="h-5 w-5 text-green-500" />
        </div> : 
        <div className="flex justify-center">
          <X className="h-5 w-5 text-red-500" />
        </div>;
    }
    return <div className="text-center font-medium">{value}</div>;
  };
  
  // Define the payment URL based on the plan
  const getPaymentUrl = (planName: PlanName) => {
    if (planName === "Starter") {
      return "https://buy.stripe.com/7sI17E39Pfsu3Zu7sG";
    } else if (planName === "Pro") {
      return "https://buy.stripe.com/cN2bMi8u9fsu0NiaET";
    } else if (planName === "Unlimited") {
      return "https://buy.stripe.com/8wM9Ea9yd6VYcw03ci";
    }
    return undefined; // Return undefined for other plans to use the default behavior
  };

  return (
    <Card className={cn("overflow-hidden transition-all", plan.highlight ? "border-[#003a70] shadow-lg" : "border-gray-200")}>
      <CardHeader className={cn("p-6", plan.highlight ? "bg-[#003a70] text-white" : plan.color)}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={cn("rounded-full p-2 flex items-center justify-center", plan.highlight ? "bg-white/20" : "bg-[#003a70]/10")}>
              {plan.icon}
            </div>
            <CardTitle className="text-xl font-bold">{plan.name}</CardTitle>
          </div>
          <Badge variant={plan.highlight ? "secondary" : "outline"} className={cn(plan.highlight ? "bg-white/20 text-white border-0" : "bg-[#003a70]/10 text-[#003a70] border-0")}>
            {plan.tagline}
          </Badge>
        </div>
        
        <div className="flex items-baseline mt-2">
          <span className="text-3xl font-bold">{plan.price}</span>
          <span className="ml-1 opacity-80">{plan.period}</span>
        </div>
        <CardDescription className={cn("mt-2", plan.highlight ? "text-white/90" : "text-gray-700")}>
          {plan.description}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="p-0">
        <div className="p-6">
          <CTAButton 
            variant={plan.highlight ? "primary" : "accent"} 
            className="w-full"
            to={getPaymentUrl(plan.name)}
          >
            {plan.ctaText}
          </CTAButton>
        </div>
        
        <div className="border-t">
          <div className="p-6">
            {featureCategories.map((category, categoryIndex) => (
              <div key={category.name} className={cn("mb-6", categoryIndex === featureCategories.length - 1 ? "mb-0" : "")}>
                <h5 className="text-sm font-bold text-[#003a70] mb-3">{category.name}</h5>
                {category.features.map(feature => (
                  <div key={feature.name} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-gray-700">{feature.name}</span>
                      {feature.description && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <HelpCircle className="h-3.5 w-3.5 text-gray-400" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs bg-[#003a70] text-white border-0">
                              <p>{feature.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    {renderFeatureValue(feature.values[plan.name])}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PlanCard;
